import { perkpointApi, type ApiPage } from "./client";
import { readEventEarn } from "@/pages/perkpoint/ruleengine/eventEarn";

export type RuleStatus = "draft" | "pending_approval" | "active" | "paused" | "archived";

export type ApiRuleAction = {
  type: string;
  points?: number;
  points_per_unit?: number;
  unit?: string;
  multiplier?: number;
  release_on?: string;
  offset_days?: number;
  metric?: string;
};

export type ApiRuleVersion = {
  version: number;
  status: string;
  trigger_event_type_key?: string | null;
  conditions?: unknown;
  actions?: ApiRuleAction[];
  earn?: {
    issuance_type: "fixed_per_txn" | "percent_of_invoice" | "points_per_spend" | "multiplier";
    award_points: number;
    min_invoice_amount: number | null;
    trigger_event_type?: string | null;
    release_on?: string | null;
    release_offset_days?: number | null;
    product_type?: string | null;
  } | null;
  redemption?: {
    min_invoice_amount: number | null;
    max_points_per_order: number;
    criteria: "percent_of_invoice" | "percent_of_points" | "none";
    criteria_pct?: number;
    earn_while_redeem: "none" | "partial" | "full";
  } | null;
  validity?: { type: "fixed" | "revolving"; days: number } | null;
  submitted_at?: string | null;
  published_at?: string | null;
  rejection_reason?: string | null;
};

export type ApiRule = {
  id: string;
  code: string;
  name: string;
  title?: string;
  description: string | null;
  status: RuleStatus;
  is_earn_rule: boolean;
  is_redemption_rule: boolean;
  is_validity_rule: boolean;
  summary: { earn?: string; redemption?: string; validity?: string };
  has_pending_changes: boolean;
  awaiting_approval: boolean;
  live_version: ApiRuleVersion | null;
  draft_version: ApiRuleVersion | null;
  usage?: { transactions: number; points_issued: number; budget_points: number | null; budget_used: number };
  created_at: string;
  updated_at: string;
  overlaps?: { code: string; name: string; reason?: string }[];
};

export type RuleStats = { active: number; paused: number; draft: number; pending_approval: number };

export type RuleAction = "submit" | "approve" | "reject" | "publish" | "pause" | "resume" | "archive";

export type NativeEarnInput = {
  issuance_type: "fixed_per_txn" | "percent_of_invoice" | "points_per_spend" | "multiplier";
  award_points: number;
  min_invoice_amount: number;
  trigger_event_type: string;
  release_on: string | null;
  release_offset_days: number | null;
  product_type: string | null;
};

export type NativeRulePart = {
  name: string;
  earn?: NativeEarnInput;
  redemption?: {
    min_invoice_amount: number | null;
    max_points_per_order: number;
    criteria: "percent_of_invoice" | "percent_of_points" | "none";
    earn_while_redeem: "none" | "partial" | "full";
  };
  validity?: { type: "fixed" | "revolving"; days: number };
};

/** The Rule Engine form's legacy payload, which the API accepts as is. */
export type RuleFormPayload = {
  title: string;
  isEarnRule: boolean;
  isRedemptionRule: boolean;
  isValidityRule: boolean;
  pointsIssuanceType?: string;
  minInvoiceAmt?: number;
  awardPoints?: number;
  burnMinInvoiceAmt?: number;
  maxRedemptionPoints?: number;
  redemptionCriteria?: string;
  earnWhileRedeem?: string;
  pointsValidityType?: string;
  pointsValidityDays?: number;
};

export const RULE_STATUS_LABELS: Record<RuleStatus, string> = {
  draft: "Draft",
  pending_approval: "Pending approval",
  active: "Live",
  paused: "Paused",
  archived: "Archived",
};

export const ruleKeys = {
  all: ["perkpoint", "rules"] as const,
  list: (programId: string, query: Record<string, unknown>) =>
    ["perkpoint", "rules", programId, "list", query] as const,
  detail: (programId: string, ruleId: string) => ["perkpoint", "rules", programId, "detail", ruleId] as const,
};

const base = (programId: string) => `/admin/programs/${programId}/rules`;

export const RulesApi = {
  async list(programId: string, query: { type?: string; status?: string; search?: string; page?: number; pageSize?: number } = {}) {
    const { data } = await perkpointApi.get<ApiPage<ApiRule> & { stats: RuleStats }>(base(programId), {
      params: query,
    });
    return data;
  },
  async get(programId: string, ruleId: string) {
    const { data } = await perkpointApi.get<ApiRule & { versions: ApiRuleVersion[] }>(
      `${base(programId)}/${ruleId}`,
    );
    return data;
  },
  async eventTypes(programId: string) {
    const { data } = await perkpointApi.get<{ key: string; name: string }[]>(
      `/admin/programs/${programId}/event-types`,
    );
    return data;
  },
  async create(programId: string, payload: RuleFormPayload | NativeRulePart) {
    const { data } = await perkpointApi.post<ApiRule>(base(programId), payload);
    return data;
  },
  async update(programId: string, ruleId: string, payload: Partial<RuleFormPayload> | NativeRulePart) {
    const { data } = await perkpointApi.patch<ApiRule>(`${base(programId)}/${ruleId}`, payload);
    return data;
  },
  async action(programId: string, ruleId: string, action: RuleAction, reason?: string) {
    const { data } = await perkpointApi.post<ApiRule>(
      `${base(programId)}/${ruleId}/${action}`,
      action === "reject" ? { reason } : {},
    );
    return data;
  },
};

/** Map the API's native parts back to the legacy form's field values. */
export function ruleFormValues(rule: ApiRule) {
  const version = rule.draft_version ?? rule.live_version;
  const eventEarn = readEventEarn(version);
  const redemption = version?.redemption;
  const validity = version?.validity;
  return {
    ruleId: rule.id,
    title: rule.name,
    earnRule: eventEarn
      ? {
          pointsIssuanceType: eventEarn.pointsIssuanceType,
          minInvoiceAmount: eventEarn.minInvoiceAmount,
          awardPoints: eventEarn.awardPoints,
          triggerEvent: eventEarn.triggerEvent,
          release: eventEarn.release,
          releaseDays: eventEarn.releaseDays,
          productType: eventEarn.productType,
          tierCredits: eventEarn.tierCredits,
        }
      : undefined,
    redemptionRule: redemption
      ? {
          minInvoiceAmount: redemption.min_invoice_amount ?? undefined,
          maxRedemptionPoints: redemption.max_points_per_order,
          redemptionCriteria:
            redemption.criteria === "percent_of_points"
              ? "%OfAvailablePoints"
              : redemption.criteria === "none"
                ? "NoCriteria"
                : "%OfInvoiceAmount",
          earnWhileRedeem:
            redemption.earn_while_redeem === "full"
              ? "FullEarn"
              : redemption.earn_while_redeem === "partial"
                ? "PartialEarn"
                : "NoEarn",
        }
      : undefined,
    validityRule: validity
      ? {
          pointsValidityType: validity.type === "revolving" ? "Revolving" : "Fixed",
          pointsValidityDays: validity.days,
        }
      : undefined,
  };
}
